/* Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved. */

import React, { useState, useEffect } from 'react';
import { X, Cloud, Droplets, Wind, Eye, Gauge, Navigation } from 'lucide-react';
import { getForecastByCoords, getWeatherIcon } from '../utils/weatherService';

const WeatherModal = ({ isOpen, onClose, weather, coordinates, streamName }) => {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    if (isOpen && coordinates) {
      loadForecast();
    }
  }, [isOpen, coordinates]);

  const loadForecast = async () => {
    setLoading(true);
    try {
      const forecastData = await getForecastByCoords(coordinates.lat, coordinates.lng);
      setForecast(forecastData);
    } catch (error) {
      console.error('Error loading forecast:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="weather-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="weather-modal-header">
          <div>
            <h2 className="modal-title">Weather Forecast</h2>
            <p className="modal-subtitle">{streamName}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div className="spinner-small" style={{ margin: '0 auto 1rem' }}></div>
            <p style={{ color: 'var(--text-medium)' }}>Loading forecast...</p>
          </div>
        ) : (
          <div className="weather-modal-body">
            {/* Current Weather */}
            {weather && (
              <div className="current-weather-card">
                <div className="current-weather-main">
                  <div className="current-weather-details">
                    <div className="current-temp">{weather.temp}°F</div>
                    <div className="current-condition">{weather.description}</div>
                    <div className="current-feels">Feels like {weather.feelsLike}°F</div>
                  </div>
                  <div className="current-icon">
                    {getWeatherIcon(weather.condition, weather.icon && weather.icon.includes('n'))}
                  </div>
                </div>
              </div>
            )}

            {/* Weather Details Grid */}
            {weather && (
              <div className="weather-details-grid">
                <div className="weather-detail-card">
                  <Droplets size={24} color="var(--primary-green)" />
                  <div>
                    <div className="detail-label">Humidity</div>
                    <div className="detail-value">{weather.humidity}%</div>
                  </div>
                </div>
                <div className="weather-detail-card">
                  <Wind size={24} color="var(--primary-green)" />
                  <div>
                    <div className="detail-label">Wind Speed</div>
                    <div className="detail-value">{weather.windSpeed} mph</div>
                  </div>
                </div>
                <div className="weather-detail-card">
                  <Eye size={24} color="var(--primary-green)" />
                  <div>
                    <div className="detail-label">Visibility</div>
                    <div className="detail-value">{Math.round(weather.visibility / 1609)} mi</div>
                  </div>
                </div>
                <div className="weather-detail-card">
                  <Gauge size={24} color="var(--primary-green)" />
                  <div>
                    <div className="detail-label">Pressure</div>
                    <div className="detail-value">{weather.pressure} hPa</div>
                  </div>
                </div>
              </div>
            )}

            {/* 5-Day Forecast */}
            {forecast && forecast.daily && forecast.daily.length > 0 && (
              <>
                <div className="forecast-section-title">5-Day Forecast</div>
                <div className="forecast-days-tabs">
                  {forecast.daily.map((day, index) => (
                    <button
                      key={index}
                      className={`forecast-day-tab ${activeDay === index ? 'active' : ''}`}
                      onClick={() => setActiveDay(index)}
                    >
                      <div className="forecast-day-date">{day.date}</div>
                      <div className="forecast-day-icon">{getWeatherIcon(day.condition)}</div>
                      <div className="forecast-day-temps">
                        <span className="temp-high">{day.tempHigh}°</span>
                        <span className="temp-low">{day.tempLow}°</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Hourly Forecast for Selected Day */}
                {forecast.daily[activeDay] && forecast.daily[activeDay].hourly && (
                  <div className="hourly-forecast">
                    <div className="hourly-forecast-title">Hourly Forecast</div>
                    <div className="hourly-forecast-scroll">
                      {forecast.daily[activeDay].hourly.map((hour, index) => (
                        <div key={index} className="hourly-forecast-item">
                          <div className="hourly-time">{hour.time}</div>
                          <div className="hourly-icon">
                            {getWeatherIcon(hour.condition, hour.icon && hour.icon.includes('n'))}
                          </div>
                          <div className="hourly-temp">{hour.temp}°F</div>
                          <div className="hourly-condition">{hour.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherModal;